/**
 * FLOYD 2026-10-26 (DEFECT 1) — a page running a superseded build.
 *
 * Every deploy publishes new content-hashed chunks and the previous deployment's chunks stop being served. A tab
 * opened BEFORE a deploy keeps running the old bundle; the first time it reaches an `import()` it asks for a chunk
 * name that the current deployment never had. On 2026-10-26 a Dayboard tab asked for
 * `assets/deleteTicketFamily-DjyUFVD5.js` after the 12:12 UTC deploy; the host answered with index.html (HTTP 200,
 * text/html — the SPA rewrite), the browser refused it as a module, and the delete was never sent.
 *
 * The existing version check (useVersionCheck) could not have warned: it compares against a hand-maintained
 * `app_version` row that production last changed on 2025-11-21. This module derives the build from the deployment
 * itself — the entry script the server serves NOW versus the one this page runs — so every deploy is noticed.
 *
 * Two states, one prompt (AppUpdateRequiredBanner):
 *   'available' — a newer deployment is being served; nothing has failed yet. Reload when convenient.
 *   'required'  — a chunk of this build could not be loaded; actions that need one cannot work until a reload.
 * No retry can fix 'required' (the bytes are gone), and nothing here reloads on its own: a person may be mid-form,
 * and an automatic reload could loop or discard what they typed.
 *
 * Vite's preload helper dispatches `vite:preloadError` for every failed dynamic import in app code; it is NEVER
 * `preventDefault`ed here, because that makes the `import()` resolve to undefined instead of rejecting — the caller
 * would carry on with a missing module rather than fail closed.
 */

const CHUNK_LOAD_ERROR_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite's CSS preload
  /ChunkLoadError|Loading chunk [\w-]+ failed/i,
  /is not a valid JavaScript MIME type/i,
  /Expected a JavaScript(?:-or-Wasm)? module script but the server responded with a MIME type of "text\/html"/i,
];

/** Is this the failure of a lazily loaded application chunk (as opposed to a network or server error)? */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown };
  const text = `${typeof e.name === 'string' ? e.name : ''} ${typeof e.message === 'string' ? e.message : String(error)}`;
  return CHUNK_LOAD_ERROR_PATTERNS.some((p) => p.test(text));
}

export const APP_UPDATED_MESSAGE =
  'OptiBay was updated while this page was open, so part of the app could not load. Reload the page, then try again.';

export type AppBuildStatus = 'current' | 'available' | 'required';
type Listener = (status: AppBuildStatus) => void;
let status: AppBuildStatus = 'current';
const listeners = new Set<Listener>();
const rank: Record<AppBuildStatus, number> = { current: 0, available: 1, required: 2 };

function raise(next: AppBuildStatus): void {
  if (rank[next] <= rank[status]) return; // only ever escalates
  status = next;
  for (const l of [...listeners]) {
    try { l(status); } catch { /* a listener never breaks the others */ }
  }
}

/** A chunk of this build could not be loaded. */
export function markAppUpdateRequired(): void { raise('required'); }
/** A newer deployment is being served. */
export function markAppUpdateAvailable(): void { raise('available'); }
export function getAppBuildStatus(): AppBuildStatus { return status; }

/** Hear the page's build status. A subscriber that arrives late is told at once. */
export function subscribeAppBuildStatus(listener: Listener): () => void {
  listeners.add(listener);
  if (status !== 'current') listener(status);
  return () => { listeners.delete(listener); };
}

/**
 * Listen, app-wide, for chunk failures: Vite's `vite:preloadError` (every lazy import in app code) and any
 * unhandled rejection that is one. Returns the uninstaller.
 */
export function installChunkLoadRecovery(target: Window = window): () => void {
  const onPreloadError = () => markAppUpdateRequired(); // deliberately NOT preventDefault() — see the header
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) markAppUpdateRequired();
  };
  target.addEventListener('vite:preloadError', onPreloadError);
  target.addEventListener('unhandledrejection', onRejection as EventListener);
  return () => {
    target.removeEventListener('vite:preloadError', onPreloadError);
    target.removeEventListener('unhandledrejection', onRejection as EventListener);
  };
}

// ── The deployment's own identity ─────────────────────────────────────────────────────────────────────────────

const ENTRY_SCRIPT = /<script\b[^>]*\btype=["']module["'][^>]*\bsrc=["']([^"']*\/assets\/index-[\w-]+\.js)["']/i;

/** The content-hashed entry script an index.html loads (`/assets/index-<hash>.js`), or null. */
export function entryScriptOf(html: string): string | null {
  const m = ENTRY_SCRIPT.exec(html);
  return m ? new URL(m[1], 'https://x.invalid/').pathname : null;
}

/** The entry script THIS page is running, or null (development server: there is no hashed entry). */
export function runningEntryScript(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  if (!el) return null;
  try { return new URL(el.src, 'https://x.invalid/').pathname; } catch { return null; }
}

/**
 * Is a different build being served than the one this page runs? Asks for index.html past every cache.
 * Unknown (development, offline, an error page) is never reported as a new build.
 */
export async function checkForNewDeployment(
  fetchImpl: typeof fetch = fetch,
  doc: Document = document,
): Promise<boolean> {
  const running = runningEntryScript(doc);
  if (!running) return false;
  try {
    const res = await fetchImpl('/', { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!res.ok) return false;
    const served = entryScriptOf(await res.text());
    if (served && served !== running) {
      markAppUpdateAvailable();
      return true;
    }
  } catch { /* offline: say nothing */ }
  return false;
}

/** Load the current deployment. Only ever called by a person's click. */
export function reloadApp(): void {
  window.location.reload();
}

/** Test seam: forget the recorded status. */
export function __resetAppBuildStatusForTests(): void {
  status = 'current';
  listeners.clear();
}
